/**
 * The audit trail.
 *
 * `cloud_audit` has been recording since migration 0001 with nothing able to
 * read it back. This is that reader.
 *
 * ── What it deliberately cannot show ────────────────────────────────────────
 * File names. The server has never known them, so a row says "shared
 * `nod_7f3…`" and this page turns the id into a name **only** for nodes the
 * browser can decrypt right now. Anything else stays an id. Rendering a blank
 * would suggest the entry is incomplete; rendering the id says exactly what is
 * known, which is the honest failure and the same choice the workspace list
 * makes for a name that will not decrypt.
 *
 * Enterprise-gated, and the reason is specific: the capability being sold is
 * seeing *other people's* actions across an organisation. Seeing your own is
 * not much of a product, and gating your own history would be gating a security
 * control — which `docs/BACKLOG.md` already settled we do not do.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, errorText, type AuditEntryWire } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { entitlements } from '@shared/entitlements';
import { PageTitle, Panel } from '../components/Shell';
import { SkeletonRows } from '../components/Skeleton';
import { accountOrigin } from './Vault';
import { formatWhen, monoPlain } from '../theme';
import { useT } from '../i18n';

/**
 * Verb → sentence.
 *
 * Actions are stable machine strings (`link.created`, `node.trashed`); this is
 * the only place they become English. An action with no entry here falls back to
 * the raw verb rather than to "unknown", so a newly added audit point shows up
 * legibly the day it ships instead of the day someone remembers this map.
 */
const PHRASING: Record<string, string> = {
  'link.created': 'created a share link',
  'link.revoked': 'revoked a share link',
  'link.opened': 'opened a share link',
  'node.created': 'created',
  'node.renamed': 'renamed',
  'node.moved': 'moved',
  'node.trashed': 'moved to trash',
  'node.restored': 'restored',
  'node.purged': 'permanently deleted',
  'node.downloaded': 'downloaded',
  'share.invited': 'shared with someone',
  'share.revoked': 'revoked a share',
  'workspace.created': 'created a workspace',
  'workspace.invited': 'invited someone to a workspace',
  'workspace.removed': 'removed someone from a workspace',
  'vault.created': 'set up their vault',
  'vault.password_changed': 'changed their vault password',
  'device.approved': 'approved a device',
  'device.revoked': 'revoked a device'
};

export function Audit() {
  const t = useT();
  const { Button, Icon } = ds();
  const { me } = useApp();

  const [entries, setEntries] = useState<AuditEntryWire[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [scope, setScope] = useState<'self' | 'organisation'>('self');
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowed = entitlements(me?.plan).auditLog;

  const load = useCallback(
    async (before?: number) => {
      before ? setMore(true) : setLoading(true);
      try {
        const res = await api.audit.list({ before, limit: 50 });
        setEntries((prev) => (before ? [...prev, ...res.entries] : res.entries));
        setCursor(res.nextCursor);
        setScope(res.scope);
        setError(null);
      } catch (err) {
        setError(errorText(err, 'Could not load the audit log.'));
      } finally {
        setLoading(false);
        setMore(false);
      }
    },
    []
  );

  useEffect(() => {
    if (allowed) void load();
    else setLoading(false);
  }, [allowed, load]);

  if (!allowed) {
    return (
      <div style={{ display: 'grid', gap: 24 }}>
        <PageTitle title={t('audit.audit-log')} meta="Part of Enterprise" />
        <Panel style={{ padding: '34px 30px', display: 'grid', gap: 16, maxWidth: 640 }}>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: 'grid',
              placeItems: 'center',
              background: 'var(--accent-100)',
              color: 'var(--accent-600)'
            }}
          >
            <Icon name="scroll-text" size={22} />
          </span>
          <h2
            style={{
              fontFamily: "var(--font-display), 'Baloo 2', system-ui",
              fontWeight: 800,
              fontSize: 24,
              margin: 0,
              color: 'var(--text-strong)'
            }}
          >
            {t('audit.every-action-on-the-record')}
          </h2>
          <span style={{ fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            {t('audit.who-shared-what-who-removed')}
          </span>
          <div>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                window.location.href = `${accountOrigin()}/pricing`;
              }}
            >
              {t('audit.see-enterprise')}
            </Button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageTitle
        title={t('audit.audit-log')}
        meta={scope === 'organisation' ? 'Everyone in your organisation' : 'Your account'}
      />

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      <Panel style={{ paddingTop: 6, paddingBottom: 6 }}>
        {loading ? (
          <SkeletonRows rows={8} label={t('audit.loading-the-audit-log')} />
        ) : entries.length === 0 ? (
          <div style={{ padding: '34px 22px' }}>
            <span style={monoPlain}>{t('audit.nothing-recorded-yet')}</span>
          </div>
        ) : (
          entries.map((e, i) => (
            <div
              key={e.id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 12,
                padding: '11px 22px',
                borderBottom:
                  i === entries.length - 1 ? 'none' : '1px solid var(--border-subtle)'
              }}
            >
              <span style={{ fontSize: 13.5, color: 'var(--text-body)', flex: 1, minWidth: 0 }}>
                {PHRASING[e.action] ?? e.action}
                {e.nodeId && (
                  <span style={{ ...monoPlain, marginLeft: 8 }}>{e.nodeId}</span>
                )}
              </span>
              {e.ip && <span style={monoPlain}>{e.ip}</span>}
              <span style={monoPlain}>{formatWhen(e.createdAt)}</span>
            </div>
          ))
        )}
      </Panel>

      {cursor !== null && (
        <div>
          <Button variant="secondary" size="md" disabled={more} onClick={() => void load(cursor)}>
            {more ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      )}

    </div>
  );
}
